// ============================================
// FRONTEND: components/List/SmartEventList.jsx
// Smart wrapper for rolling window infinite scroll
// Supports both vertical and horizontal layouts
// ============================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import SimpleScrollList from '../SimpleScrollList/SimpleScrollList';
import SimpleHorizontalList from '../SimpleScrollList/SimpleHorizontalList';
import SmoothScrollList from '../SmoothScrollList/SmoothScrollList';
import { useFeedBatches } from '../../hooks/useFeedBatches';
import EventExpandedView from '../PlaceDetails/EventExpandedView';

/**
 * SmartEventList - Intelligent event list with rolling window infinite scroll
 *
 * Features:
 * - Maintains fixed window of 15 events
 * - Bidirectional infinite scroll (load next/previous batches)
 * - Smooth transitions without focus loss
 * - Works with both vertical and horizontal layouts
 * - Optional smooth scroll mode using transform-based rendering
 *
 * PERFORMANCE OPTIONS:
 * - Use scrollMode='native' (default) for standard browser scrolling with optimizations
 * - Use scrollMode='smooth' for transform-based scrolling (better for some mobile devices)
 */
const SmartEventList = React.forwardRef(({
  layout = 'vertical', // 'vertical' | 'horizontal'
  scrollMode = 'native', // 'native' | 'smooth' - PERFORMANCE: choose scroll implementation
  userId = null,
  language = 'EN',
  lat = null,
  lng = null,
  userLocation = null, // Accept userLocation object {latitude, longitude}
  onLocationClick,
  tracker = null,
  enabled = true,
  childClicked = null,
  onActiveIndexChange = null,
  onItemsChange = null, // NEW: Callback to expose current items to parent
  onMapSyncNeeded = null, // NEW: Callback for map synchronization when active item changes
  preserveScrollOnPrepend = true, // Pass-through flag for scroll compensation control
  searchQuery = '',        // NEW: optional text search
  startDate = null,        // NEW: optional start date filter (YYYY-MM-DD)
  endDate = null,          // NEW: optional end date filter (YYYY-MM-DD)
  source,                  // NEW: Which collections to search (['events'], ['reviews'], etc.)
  excludeCategories,       // NEW: Categories to exclude from results
  radiusKm = 50,           // NEW: Radius in km for geo filter
  // Pass-through props for list components (NOTE: events/places props are explicitly excluded)
  context,
  feedMode,
  category,
  showPager,
  showCounter,
  isLoading,
  hasSearched,
  expandedPlace: controlledExpandedPlace,
  onExpand: controlledOnExpand,
  onExpandClose: controlledOnExpandClose,
  ...otherProps // Remaining props (explicitly excluding events/places to prevent override)
}, ref) => {
  // Extract lat/lng from userLocation if not provided directly
  const finalLat = lat ?? userLocation?.latitude ?? null;
  const finalLng = lng ?? userLocation?.longitude ?? null;

  console.log('🔍 [SmartEventList] Received searchQuery:', searchQuery);
  console.log('🏷️ [SmartEventList] Received category:', category);
  console.log('⚙️ [SmartEventList] feedMode:', feedMode);
  console.log('[SmartEventList] Full props:', {
    layout,
    language,
    userId,
    userLocation: userLocation,
    lat: finalLat,
    lng: finalLng,
    searchQuery: searchQuery,
    category: category,
    feedMode: feedMode,
    enabled
  });

  // Use rolling window hook
  const {
    items,
    focusIndex,
    focusIsAuthoritative, // NEW: Whether focus was truly preserved (vs weak fallback)
    isLoadingNext,
    isLoadingPrevious,
    isInitialized,
    error,
    hasNext,
    hasPrevious,
    handleFocusChange,
    loadNext,
    loadPrevious,
    isShiftingWindow, // NEW: Flag to prevent interference during window shifts
    pendingScrollAdjustment, // Get scroll adjustment from hook
    onScrollAdjustmentApplied, // Callback to clear adjustment when list is done
    resetAndInitialize,   // NEW: Reset and re-initialize on searchQuery change
  } = useFeedBatches({
    userId,
    language,
    lat: finalLat,
    lng: finalLng,
    enabled,
    searchQuery,         // NEW: optional param
    category,            // NEW: optional category filter
    startDate,           // NEW: optional start date filter
    endDate,             // NEW: optional end date filter
    source,              // NEW: Which collections to search
    excludeCategories,   // NEW: Categories to exclude
    radiusKm             // NEW: Radius in km
  });

  // Expose methods to parent components via ref
  React.useImperativeHandle(ref, () => ({
    loadNext
  }));

  // Expanded item overlay state (controlled or uncontrolled fallback)
  const [localExpandedPlace, setLocalExpandedPlace] = useState(null);
  const expandedPlace = controlledExpandedPlace !== undefined ? controlledExpandedPlace : localExpandedPlace;

  const handleItemExpand = useCallback((place) => {
    if (controlledOnExpand) {
      controlledOnExpand(place);
    } else {
      setLocalExpandedPlace(place);
    }
  }, [controlledOnExpand]);

  const handleExpandClose = useCallback(() => {
    if (controlledOnExpandClose) {
      controlledOnExpandClose();
    } else {
      setLocalExpandedPlace(null);
    }
  }, [controlledOnExpandClose]);

  // State for skeleton items (visual placeholders during loading)
  const [displayItems, setDisplayItems] = useState([]);
  const previousItemsRef = useRef([]);
  const listRef = useRef(null);

  // Track what the list told us its active index is, for edge demand
  const lastListActiveIndexRef = useRef(null);
  const lastListActiveEventIdRef = useRef(null);

  // Local shifting state - provides faster unlock for child components
  const [localShifting, setLocalShifting] = useState(false);

  // Track last search key to trigger reset when it changes
  const [lastSearchKey, setLastSearchKey] = useState(null);

  /**
   * NEW: Handle edge demand from lists (when user scrolls to edges)
   * This forwards "I reached the edge" events from lists to the feed hook
   *
   * ✅ OPTION B: When horizontal list explicitly fires onEdgeDemand('down'),
   * we attempt to load next even if hasNext is false - as long as we're not already loading.
   * This fixes the "dead end" problem where a single missing cursor prevents further loading.
   */
  const handleEdgeDemand = useCallback((direction) => {
    // direction will be 'down' (scroll right / scroll down) or 'up'
    if (direction === "down") {
      // 1) if we actually know what the list was showing, tell the hook NOW
      const idx = lastListActiveIndexRef.current;
      const evId = lastListActiveEventIdRef.current;
      if (typeof idx === "number" && idx >= 0) {
        // this will update useFeedBatches' internal "currentFocusEventIdRef"
        handleFocusChange(idx, evId);
      }

      // 2) then try to load
      if (!isLoadingNext) {
        console.log(
          "[SmartEventList] onEdgeDemand('down') → attempting loadNext() even if hasNext is false (with last active index)",
          { idx, evId }
        );
        loadNext();
      } else {
        console.log(
          "[SmartEventList] onEdgeDemand('down') ignored because isLoadingNext=true"
        );
      }
    } else if (direction === "up") {
      // Keep upward scroll behavior strict (optional but recommended)
      if (hasPrevious && !isLoadingPrevious) {
        loadPrevious();
      }
    }
  }, [
    loadNext,
    loadPrevious,
    isLoadingNext,
    isLoadingPrevious,
    hasPrevious,
    handleFocusChange, // ← make sure this is in deps
  ]);

  /**
   * Manage local shifting state - unlocks children as soon as parent says done
   */
  useEffect(() => {
    setLocalShifting(isShiftingWindow);
  }, [isShiftingWindow]);

  /**
   * Reset and re-initialize feed ONLY when searchQuery changes (explicit search trigger)
   * Category, dates, and other filters are applied but don't trigger auto-search
   */
  useEffect(() => {
    if (!feedMode) return;

    // Reset on searchQuery, source, or excludeCategories change (tab switch or search trigger)
    const key = JSON.stringify({
      language,
      lat: finalLat,
      lng: finalLng,
      searchQuery: searchQuery || '',
      source: source || ['events'],
      excludeCategories: excludeCategories || [],
      category: category || null,
      startDate: startDate || null,
      endDate: endDate || null,
    });

    console.log('[SmartEventList] Search key check:', {
      newKey: key,
      lastKey: lastSearchKey,
      willReset: key !== lastSearchKey,
      searchQuery: searchQuery,
      source: source,
    });

    if (key === lastSearchKey) return;
    setLastSearchKey(key);

    console.log('🔄 [SmartEventList] Resetting feed due to search/tab change:', { searchQuery, source, category });
    console.log('📋 [SmartEventList] Filter values to apply:', { category, startDate, endDate, excludeCategories });

    // Reset feed window and reload with new search query
    // Dates and category are included in API request but didn't trigger this reset
    resetAndInitialize();
  }, [feedMode, language, finalLat, finalLng, searchQuery, source, excludeCategories, category, resetAndInitialize, lastSearchKey]);

  /**
   * Update display items with loading skeletons
   * CRITICAL FIX: Client-side deduplication to prevent duplicate keys
   */
  useEffect(() => {
    if (items.length === 0) {
      setDisplayItems([]);
      return;
    }

    // CLIENT-SIDE DEDUPLICATION: Remove duplicates by event_id/id
    const seenIds = new Set();
    const deduplicatedItems = items.filter(item => {
      if (item.__skeleton) return true; // Keep skeletons

      const itemId = String(item.event_id || item.id || '');
      if (!itemId) return true; // Keep items without IDs (shouldn't happen)

      if (seenIds.has(itemId)) {
        console.warn('[SmartEventList] 🚫 Duplicate item filtered:', itemId);
        return false; // Skip duplicate
      }

      seenIds.add(itemId);
      return true; // Keep unique items
    });

    if (deduplicatedItems.length !== items.length) {
      console.warn('[SmartEventList] ⚠️ Filtered', items.length - deduplicatedItems.length, 'duplicate items');
    }

    let newDisplayItems = [...deduplicatedItems];

    // Add skeleton placeholders during loading
    if (isLoadingNext) {
      // Add skeletons at the end
      const skeletons = Array(5).fill(null).map((_, i) => ({
        __skeleton: true,
        id: `skeleton-next-${i}`,
        event_id: `skeleton-next-${i}`
      }));
      newDisplayItems = [...newDisplayItems, ...skeletons];
    }

    if (isLoadingPrevious) {
      // Add skeletons at the beginning
      const skeletons = Array(5).fill(null).map((_, i) => ({
        __skeleton: true,
        id: `skeleton-prev-${i}`,
        event_id: `skeleton-prev-${i}`
      }));
      newDisplayItems = [...skeletons, ...newDisplayItems];
    }

    setDisplayItems(newDisplayItems);
    previousItemsRef.current = deduplicatedItems;
  }, [items, isLoadingNext, isLoadingPrevious]);

  /**
   * NEW: Expose items to parent whenever they change
   * STABILIZATION: Suppressed during window shifts to prevent parent interference
   *
   * This callback enables the parent (e.g., MobileLayout) to:
   * - Track current items for map synchronization
   * - Preserve focused event when switching between vertical/horizontal layouts
   * - The parent can find the event by ID (not index) and restore focus after layout switches
   */
  useEffect(() => {
    if (onItemsChange && !isShiftingWindow && !localShifting) {
      onItemsChange(items);
    } else if (isShiftingWindow || localShifting) {
      console.log('[SmartEventList] 🚫 Stabilizing → skipping onItemsChange');
    }
  }, [items, onItemsChange, isShiftingWindow, localShifting]);

  /**
   * Helper: Normalize coordinates from item with multiple possible field names
   * Returns {lat, lng} or null if invalid
   */
  const getItemLatLng = useCallback((item) => {
    if (!item) return null;

    const rawLat =
      item.latitude ??
      item.lat ??
      item.location_latitude ??
      item.location_lat;

    const rawLng =
      item.longitude ??
      item.lng ??
      item.location_longitude ??
      item.location_lng;

    if (rawLat == null || rawLng == null) return null;

    const lat = Number(rawLat);
    const lng = Number(rawLng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return { lat, lng };
  }, []);

  /**
   * Handle active index change from list component
   * Maps to rolling window focus index
   */
  const handleListActiveIndexChange = useCallback((listIndex, item) => {
    // If skeletons are prepended, adjust the index
    const actualIndex = isLoadingPrevious ? listIndex - 5 : listIndex;

    // Clamp to valid range
    const clampedIndex = Math.max(0, Math.min(items.length - 1, actualIndex));

    // Extract event ID from item
    const eventId = item?.id ?? item?.event_id;

    // 👉 Store what the list says is current, so we can reuse on edge
    lastListActiveIndexRef.current = clampedIndex;
    lastListActiveEventIdRef.current = eventId;

    // Update rolling window focus with BOTH index and event ID
    handleFocusChange(clampedIndex, eventId);

    // Skip parent notification during shift
    if (isShiftingWindow || localShifting) {
      console.log('[SmartEventList] 🚫 Stabilizing → skipping onActiveIndexChange (index', clampedIndex, ')');
      return;
    }

    // Notify parent component of active index change
    if (onActiveIndexChange) {
      onActiveIndexChange(clampedIndex);
    }

    // Notify parent for map synchronization with normalized coordinates
    const activeItem = items[clampedIndex];
    if (onMapSyncNeeded && activeItem) {
      const coords = getItemLatLng(activeItem);

      if (coords) {
        // keep the same API: (lat, lng, index)
        onMapSyncNeeded(coords.lat, coords.lng, clampedIndex);
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[SmartEventList] No valid coordinates for active item', {
          index: listIndex,
          clampedIndex,
          activeItem,
        });
      }
    }
  }, [items, isLoadingPrevious, isShiftingWindow, localShifting, handleFocusChange, onActiveIndexChange, onMapSyncNeeded, getItemLatLng]);

  /**
   * Calculate childClicked index for list component
   * Accounts for skeleton placeholders
   */
  const getChildClickedIndex = useCallback(() => {
    if (childClicked === null) return null;
    
    // If skeletons are prepended, offset the index
    return isLoadingPrevious ? childClicked + 5 : childClicked;
  }, [childClicked, isLoadingPrevious]);

  /**
   * Render loading state
   */
  if (!isInitialized && !error) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3
      }}>
        <Box sx={{ color: 'error.main', textAlign: 'center' }}>
          {error}
        </Box>
      </Box>
    );
  }

  /**
   * PERFORMANCE: Select appropriate list component based on layout and scroll mode
   *
   * Scroll Mode Options:
   * - 'native' (default): Uses browser native scrolling with performance optimizations
   *   - Throttled scroll detection (100ms = 10fps)
   *   - Debounced parent notifications (200ms)
   *   - requestAnimationFrame for DOM measurements
   *   - Best for most use cases
   *
   * - 'smooth': Uses CSS transform-based scrolling (SmoothScrollList)
   *   - Physics-based momentum scrolling
   *   - No layout thrashing from scroll events
   *   - Better for some mobile devices with poor scroll performance
   *   - Only works with vertical layout
   *
   * To switch to smooth scroll mode:
   * <SmartEventList scrollMode="smooth" layout="vertical" ... />
   */
  const ListComponent = (() => {
    // Horizontal layout always uses SimpleHorizontalList
    if (layout === 'horizontal') {
      return SimpleHorizontalList;
    }

    // Vertical layout: choose based on scroll mode
    if (scrollMode === 'smooth') {
      return SmoothScrollList;
    }

    // Default: native scroll with optimizations
    return SimpleScrollList;
  })();

  // Pass-through flag: some screens want free scrolling and don't want
  // the list to auto-correct scrollTop after a prepend.
  //
  // IMPORTANT: childClicked can be null to indicate "do not programmatically scroll right now"
  // This prevents feedback loops where the list reports scroll position via onActiveIndexChange
  // and the parent echoes it back, causing unwanted scrollIntoView calls.
  //
  // CRITICAL: Filter out events/places from otherProps to prevent parent override
  const { events, places, ...safeOtherProps } = otherProps;

  // Only pin to parent when parent is sure about the focus
  const targetIndex = focusIsAuthoritative ? focusIndex : null;

  // Only send post-shift anchor when actively shifting AND focus is authoritative
  const shouldSendAnchor =
    layout === "horizontal" && isShiftingWindow && focusIsAuthoritative;

  return (
    <Box sx={{ height: '100%', position: 'relative' }} ref={listRef}>
      {/* Expanded item overlay — covers list area only, not the map */}
      {expandedPlace && (
        <EventExpandedView place={expandedPlace} onClose={handleExpandClose} />
      )}

      <ListComponent
        places={displayItems}
        childClicked={localShifting ? null : getChildClickedIndex()}
        isLoading={isLoading !== undefined ? isLoading : false}
        onLocationClick={onLocationClick}
        language={language}
        onActiveIndexChange={handleListActiveIndexChange}
        tracker={tracker}
        hasSearched={hasSearched !== undefined ? hasSearched : true}
        listRef={listRef}
        pendingScrollAdjustment={pendingScrollAdjustment}
        onScrollAdjustmentApplied={onScrollAdjustmentApplied}
        isShiftingWindow={localShifting}
        isLoadingNext={isLoadingNext}
        isLoadingPrevious={isLoadingPrevious}
        preserveScrollOnPrepend={preserveScrollOnPrepend}
        targetIndex={targetIndex} // Only pin when authoritative
        suppressAutoScroll={localShifting}
        context={context}
        feedMode={feedMode}
        category={category}
        showPager={showPager}
        showCounter={showCounter}
        // Expand handler — passed down to PlaceDetails via SimpleScrollList → VerticalEventRow
        onExpand={handleItemExpand}
        // NEW: Edge demand channel for horizontal list
        onEdgeDemand={handleEdgeDemand}
        // NEW: Authoritative focus props for horizontal list
        authoritativeFocusIndex={layout === "horizontal" ? targetIndex : undefined}
        authoritativeFocusEventId={
          layout === "horizontal"
            ? (items[targetIndex]?.id || items[targetIndex]?.event_id)
            : undefined
        }
        // NEW: Post-shift anchor props for horizontal list stability
        postShiftAnchorIndex={shouldSendAnchor ? targetIndex : undefined}
        postShiftAnchorEventId={
          shouldSendAnchor
            ? (items[targetIndex]?.id || items[targetIndex]?.event_id)
            : undefined
        }
        postShiftAnchorAt={shouldSendAnchor ? Date.now() : undefined}
        {...safeOtherProps}
      />

      {/* Loading indicators */}
      {isLoadingNext && (
        <Box sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 1000
        }}>
          <CircularProgress size={16} color="inherit" />
          <Box sx={{ fontSize: '0.875rem' }}>Loading more...</Box>
        </Box>
      )}

      {isLoadingPrevious && (
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 1000
        }}>
          <CircularProgress size={16} color="inherit" />
          <Box sx={{ fontSize: '0.875rem' }}>Loading previous...</Box>
        </Box>
      )}

    </Box>
  );
});

export default React.memo(SmartEventList);